// ============================================
// Leka — Login Page (v9: 6-digit OTP — Dark Premium)
// ============================================
import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { authClient } from '@/shared/lib/auth-client'
import { Spinner } from '@/shared/components/Spinner'

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .leka-fadein {
      animation: none !important;
    }
  }

  .leka-fadein {
    animation: fadeInUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .leka-fadein-1 { animation-delay: 0.05s; }
  .leka-fadein-2 { animation-delay: 0.12s; }
  .leka-fadein-3 { animation-delay: 0.20s; }
  .leka-fadein-4 { animation-delay: 0.28s; }
  .leka-fadein-5 { animation-delay: 0.36s; }

  .otp-input {
    width: 44px;
    height: 52px;
    text-align: center;
    font-size: 22px;
    font-weight: 500;
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    color: rgba(255,255,255,0.90);
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    outline: none;
    transition: background 0.18s, border-color 0.18s;
    caret-color: #E8D48A;
  }

  .otp-input:focus {
    background: rgba(255,255,255,0.09);
    border-color: rgba(232,212,138,0.5);
  }

  .otp-input::placeholder {
    color: rgba(255,255,255,0.15);
  }
`

export function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [email, setEmail]       = useState('')
  const [checking, setChecking] = useState(true)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [step, setStep]         = useState<'email' | 'otp'>('email')
  const [otp, setOtp]           = useState(['', '', '', '', '', ''])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    authClient.getSession().then(({ data }: { data: { session?: unknown } | null }) => {
      if (data?.session) {
        const redirect = searchParams.get('redirect')
        const safeRedirect = redirect && /^\/[^/]/.test(redirect) ? redirect : '/'
        navigate(safeRedirect, { replace: true })
      } else {
        setChecking(false)
      }
    }).catch(() => {
      setChecking(false)
    })
  }, [navigate, searchParams])

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#080808' }}>
        <Spinner size="md" />
      </div>
    )
  }

  const getCallbackURL = () => {
    const redirect = searchParams.get('redirect')
    if (redirect && /^\/[^/]/.test(redirect)) {
      return `https://tryleka.com${redirect}`
    }
    return 'https://tryleka.com/'
  }

  const handleGoogleSignIn = () => {
    setLoading(true)
    setError('')
    const callbackURL = encodeURIComponent(getCallbackURL())
    window.location.href = `/login/google?callbackURL=${callbackURL}`
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')

    try {
      const { error: authError } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'sign-in',
      })
      if (authError) {
        setError((authError as { message?: string }).message || 'Error. Please try again.')
        setLoading(false)
        return
      }
      setStep('otp')
      setLoading(false)
      // Focus first OTP input after render
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } catch {
      setError('Network error. Please check your connection and try again.')
      setLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1)
    const newOtp = [...otp]
    newOtp[index] = digit
    setOtp(newOtp)

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const fullOtp = newOtp.join('')
      if (fullOtp.length === 6) {
        submitOtp(fullOtp)
      }
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const newOtp = [...otp]
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pasted[i] || ''
    }
    setOtp(newOtp)
    if (pasted.length === 6) {
      submitOtp(pasted)
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus()
    }
  }

  const submitOtp = async (code: string) => {
    setLoading(true)
    setError('')
    try {
      const { error: authError } = await authClient.signIn.emailOtp({
        email,
        otp: code,
      })
      if (authError) {
        setError((authError as { message?: string }).message || 'Invalid code. Please try again.')
        setOtp(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
        setLoading(false)
        return
      }
      // Success — redirect
      const redirect = searchParams.get('redirect')
      const safeRedirect = redirect && /^\/[^/]/.test(redirect) ? redirect : '/'
      navigate(safeRedirect, { replace: true })
    } catch {
      setError('Network error. Please try again.')
      setOtp(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
      setLoading(false)
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#080808',
          position: 'relative',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '24px',
        }}
      >
        {/* Gold radial glow */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(180,140,20,0.13) 0%, rgba(140,100,10,0.06) 50%, transparent 100%)',
            zIndex: 0,
          }}
        />

        {/* Card */}
        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '380px' }}>
          {step === 'email' ? (
            <>
              {/* Logo */}
              <div className="leka-fadein leka-fadein-1" style={{ textAlign: 'center', marginBottom: '36px' }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <span style={{
                    fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                    fontSize: '28px', fontWeight: 300, letterSpacing: '0.25em',
                    color: '#E8D48A', textTransform: 'uppercase' as const,
                  }}>
                    LEKA
                  </span>
                </Link>
              </div>

              {/* Headline */}
              <div className="leka-fadein leka-fadein-2" style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h1 style={{
                  fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                  fontSize: '30px', fontWeight: 300, fontStyle: 'italic',
                  color: 'rgba(255,255,255,0.90)', margin: 0, lineHeight: 1.2,
                }}>
                  Welcome back
                </h1>
              </div>

              {/* Google button */}
              <div className="leka-fadein leka-fadein-3">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    padding: '14px 20px', fontSize: '14px', fontWeight: 500,
                    color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.14)', borderRadius: '10px',
                    cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
                    transition: 'background 0.18s, border-color 0.18s',
                  }}
                  onMouseEnter={e => { if (!loading) { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.11)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.24)' }}}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.07)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.14)' }}
                >
                  {loading ? <Spinner size="sm" /> : (
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Continue with Google
                </button>
              </div>

              {/* Divider */}
              <div className="leka-fadein leka-fadein-4" style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.09)' }} />
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.22)', letterSpacing: '0.12em', fontWeight: 500 }}>— or —</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.09)' }} />
              </div>

              {/* Email form */}
              <div className="leka-fadein leka-fadein-5">
                <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    style={{
                      width: '100%', padding: '13px 16px', fontSize: '14px',
                      color: 'rgba(255,255,255,0.88)', background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.10)', borderRadius: '10px',
                      outline: 'none', transition: 'background 0.18s, border-color 0.18s',
                      boxSizing: 'border-box' as const,
                    }}
                    onFocus={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)' }}
                    onBlur={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)' }}
                  />
                  <button
                    type="submit"
                    disabled={loading || !email}
                    style={{
                      width: '100%', padding: '13px 20px', fontSize: '14px', fontWeight: 500,
                      color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.14)', borderRadius: '10px',
                      cursor: loading ? 'not-allowed' : 'pointer', opacity: loading || !email ? 0.45 : 1,
                      transition: 'color 0.18s, border-color 0.18s, background 0.18s',
                    }}
                    onMouseEnter={e => { if (!loading && email) { const el = e.currentTarget; el.style.color = 'rgba(255,255,255,0.88)'; el.style.borderColor = 'rgba(255,255,255,0.28)'; el.style.background = 'rgba(255,255,255,0.09)' }}}
                    onMouseLeave={e => { const el = e.currentTarget; el.style.color = 'rgba(255,255,255,0.65)'; el.style.borderColor = 'rgba(255,255,255,0.14)'; el.style.background = 'rgba(255,255,255,0.06)' }}
                  >
                    {loading ? 'Sending…' : 'Continue with email'}
                  </button>

                  {error && (
                    <p style={{ fontSize: '12px', color: '#f87171', textAlign: 'center', margin: 0 }}>{error}</p>
                  )}
                </form>
              </div>

              {/* Footer links */}
              <div style={{ textAlign: 'center', marginTop: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                <Link to="/privacy" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', textDecoration: 'none' }}>Privacy</Link>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.12)' }}>·</span>
                <Link to="/terms" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', textDecoration: 'none' }}>Terms</Link>
              </div>
            </>
          ) : (
            /* OTP Step */
            <div className="leka-fadein leka-fadein-1" style={{ textAlign: 'center' }}>
              {/* Logo */}
              <div style={{ marginBottom: '32px' }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <span style={{
                    fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                    fontSize: '28px', fontWeight: 300, letterSpacing: '0.25em',
                    color: '#E8D48A', textTransform: 'uppercase' as const,
                  }}>
                    LEKA
                  </span>
                </Link>
              </div>

              {/* Lock icon */}
              <div style={{
                width: '48px', height: '48px', margin: '0 auto 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '12px',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>

              <h2 style={{
                fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                fontSize: '26px', fontWeight: 300, fontStyle: 'italic',
                color: 'rgba(255,255,255,0.90)', margin: '0 0 8px',
              }}>
                Enter your code
              </h2>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)', margin: '0 0 4px' }}>
                We sent a 6-digit code to
              </p>
              <p style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.75)', margin: '0 0 28px' }}>
                {email}
              </p>

              {/* OTP inputs */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => { inputRefs.current[i] = el }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className="otp-input"
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    disabled={loading}
                    placeholder="·"
                  />
                ))}
              </div>

              {loading && (
                <div style={{ marginBottom: '16px' }}>
                  <Spinner size="sm" />
                </div>
              )}

              {error && (
                <p style={{ fontSize: '12px', color: '#f87171', textAlign: 'center', margin: '0 0 16px' }}>{error}</p>
              )}

              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', marginTop: '8px' }}>
                Didn't get it?{' '}
                <button
                  onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setError('') }}
                  style={{
                    fontSize: '12px', color: 'rgba(255,255,255,0.40)', background: 'none',
                    border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0,
                  }}
                >
                  Try again
                </button>
              </p>
            </div>
          )}

          {/* Back link */}
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link to="/" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.20)', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
